import React, { useState } from 'react';
import { Copy, Check, KeyRound, AlertTriangle } from 'lucide-react';

interface AnonymousBookingCredentialsProps {
  email: string;
  generatedPassword: string;
}

/**
 * Component to display generated credentials after an anonymous booking.
 * Shows the password prominently with copy functionality.
 */
export const AnonymousBookingCredentials: React.FC<AnonymousBookingCredentialsProps> = ({
  email,
  generatedPassword
}) => {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyToClipboard = (text: string, type: 'email' | 'password') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'email') {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      }
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg p-4 my-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
            Save Your Login Details
          </h3>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            An account has been created for you. Save these credentials to log in and manage your bookings in the future.
          </p>

          {/* Email */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
              Email (Username)
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-600 rounded px-3 py-2 font-mono text-sm text-gray-800 dark:text-gray-200">
                {email}
              </div>
              <button
                onClick={() => copyToClipboard(email, 'email')}
                className="flex items-center justify-center w-10 h-10 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded transition-colors"
                title="Copy email"
              >
                {copiedEmail ? (
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                )}
              </button>
            </div>
          </div>

          {/* Password */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
              Password
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-600 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="font-mono text-lg font-semibold text-gray-800 dark:text-gray-200">
                    {generatedPassword}
                  </span>
                </div>
              </div>
              <button
                onClick={() => copyToClipboard(generatedPassword, 'password')}
                className="flex items-center justify-center w-10 h-10 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded transition-colors"
                title="Copy password"
              >
                {copiedPassword ? (
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                )}
              </button>
            </div>
          </div>

          <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
            You can change your password after logging in from your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnonymousBookingCredentials;
